import React from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import type { ProgressStep } from './ai-types'

/**
 * Props for ProgressTracker component
 */
interface ProgressTrackerProps {
  /** Array of step labels to display */
  steps: Array<{ key: ProgressStep; label: string; loadingLabel?: string }>
  /** Current active step (null if not started) */
  currentStep: ProgressStep | null
  /** Array of completed step keys */
  completedSteps: ProgressStep[]
}

/**
 * Reusable progress tracker component
 * Displays step-based progress with icons (loading spinner, checkmark, or empty)
 */
export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  currentStep,
  completedSteps,
}) => (
  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
      Progress
    </Typography>

    {steps.map((step) => {
      const isCompleted = completedSteps.includes(step.key)
      const isCurrent = currentStep === step.key

      return (
        <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {isCompleted && <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />}
          {!isCompleted && isCurrent && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {!isCompleted && !isCurrent && <Box sx={{ width: 20, height: 20, mr: 1 }} />}
          <Typography variant="body2">
            {isCurrent && step.loadingLabel ? step.loadingLabel : step.label}
          </Typography>
        </Box>
      )
    })}
  </Box>
)
